/**
 * DiffViewer — before/after highlighted split view (minimal, no external diff lib).
 * Used by ProposalPreview and Quick Actions "Comparar".
 */
import { cn } from "@/lib/utils";
import { ArrowRight } from "lucide-react";

interface DiffViewerProps {
  before: string;
  after: string;
  label?: string;
  className?: string;
  /** Word-level highlight instead of full-line coloring. Default: true. */
  wordLevel?: boolean;
}

/** Simple word-diff: returns runs of [unchanged|removed|added, text][]. */
function wordDiff(
  before: string,
  after: string,
): Array<{ type: "same" | "removed" | "added"; text: string }> {
  const bWords = before.split(/(\s+)/);
  const aWords = after.split(/(\s+)/);
  const result: Array<{ type: "same" | "removed" | "added"; text: string }> = [];

  // Simple LCS-based diff
  const m = bWords.length;
  const n = aWords.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = bWords[i - 1] === aWords[j - 1]
        ? dp[i - 1][j - 1] + 1
        : Math.max(dp[i - 1][j], dp[i][j - 1]);
    }
  }

  let i = m, j = n;
  const ops: Array<{ type: "same" | "removed" | "added"; text: string }> = [];
  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && bWords[i - 1] === aWords[j - 1]) {
      ops.unshift({ type: "same", text: bWords[i - 1] });
      i--; j--;
    } else if (j > 0 && (i === 0 || dp[i][j - 1] >= dp[i - 1][j])) {
      ops.unshift({ type: "added", text: aWords[j - 1] });
      j--;
    } else {
      ops.unshift({ type: "removed", text: bWords[i - 1] });
      i--;
    }
  }
  return ops;
}

function WordDiffView({ before, after }: { before: string; after: string }) {
  const ops = wordDiff(before, after);

  return (
    <div className="rounded-lg border bg-muted/30 p-3 text-xs leading-relaxed">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Antes</p>
          <p className="break-words whitespace-pre-wrap">
            {ops.map((op, i) =>
              op.type === "removed" ? (
                <mark key={i} className="bg-red-100 text-red-700 dark:bg-red-950/60 dark:text-red-300 rounded px-0.5">
                  {op.text}
                </mark>
              ) : op.type === "same" ? (
                <span key={i}>{op.text}</span>
              ) : null,
            )}
          </p>
        </div>
        <ArrowRight className="mt-5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <p className="mb-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Después</p>
          <p className="break-words whitespace-pre-wrap">
            {ops.map((op, i) =>
              op.type === "added" ? (
                <mark key={i} className="bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 rounded px-0.5">
                  {op.text}
                </mark>
              ) : op.type === "same" ? (
                <span key={i}>{op.text}</span>
              ) : null,
            )}
          </p>
        </div>
      </div>
    </div>
  );
}

function SplitView({ before, after }: { before: string; after: string }) {
  return (
    <div className="grid grid-cols-2 gap-2 rounded-lg border text-xs overflow-hidden">
      <div className="bg-red-50/70 dark:bg-red-950/20 p-3">
        <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-red-600 dark:text-red-400">
          Antes
        </p>
        <pre className="whitespace-pre-wrap break-words font-sans text-red-900 dark:text-red-100">
          {before || "(vacío)"}
        </pre>
      </div>
      <div className="bg-emerald-50/70 dark:bg-emerald-950/20 p-3">
        <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wide text-emerald-600 dark:text-emerald-400">
          Después
        </p>
        <pre className="whitespace-pre-wrap break-words font-sans text-emerald-900 dark:text-emerald-100">
          {after || "(vacío)"}
        </pre>
      </div>
    </div>
  );
}

export function DiffViewer({
  before,
  after,
  label,
  className,
  wordLevel = true,
}: DiffViewerProps) {
  const useSplit = !wordLevel || before.length > 300 || after.length > 300;

  return (
    <div className={cn("space-y-1.5", className)}>
      {label && (
        <p className="text-xs font-medium text-muted-foreground">{label}</p>
      )}
      {useSplit ? (
        <SplitView before={before} after={after} />
      ) : (
        <WordDiffView before={before} after={after} />
      )}
    </div>
  );
}
